// Emissão de NFS-e (padrão nacional / DPS via ACBr API).
// Também usado para corrigir uma NFS-e rejeitada (notaRejeitada) e para substituir uma autorizada (notaSubstituida).
import React, { useEffect, useRef, useState } from "react";
import { abrirArquivo, fiscal } from "../api";
import Destinatario from "../components/Destinatario";
import { Aviso, Botao, Campo, Cartao, Entrada, Numero, Selecao, Texto, Valor } from "../components/ui";
import { brl, hojeISO, round2, soDigitos } from "../utils";

const INICIAL = {
  destinatario: {},
  servico: { servico_id: null, codigo_tributacao_nacional: "", codigo_tributacao_municipal: "", nbs: "", cnae: "", discriminacao: "",
    incidencia_local_prestacao: false, ibs_cbs_cst: "000", ibs_cbs_class_trib: "000001", indicador_operacao: "" },
  competencia: hojeISO(),
  municipio_prestacao: "",
  valor_servico: 0,
  desconto_incondicionado: 0,
  desconto_condicionado: 0,
  deducao_valor: 0,
  deducao_percentual: 0,
  obra: { cno: "", inscricao_imobiliaria: "" },
  exportacao: null,
  iss: { tributacao: 1, aliquota: 0, retido: false },
  retencoes: { pis_cofins_cst: "", aliquota_pis: 0, aliquota_cofins: 0, pis_cofins_retidos: false, irrf: 0, csll: 0, inss: 0 },
  consumidor_final: false,
  informacoes_complementares: "",
  enviar_email: true,
};

const EXPORT_PADRAO = { modo_prestacao: 1, vinculo: 0, moeda: "220", valor_moeda: 0, mecanismo_prestador: "01",
  mecanismo_tomador: "01", movimentacao_bens: 1, enviar_mdic: false };
const EXT_VAZIO = { pais: "", codigo_postal: "", cidade: "", estado_provincia: "", logradouro: "", numero: "", bairro: "" };
const SIMPLES = ["1", "2", "4"];

export function corpoNfse(n) {
  const est = Boolean(n.destinatario?.estrangeiro);
  const obra = n.obra?.cno || n.obra?.inscricao_imobiliaria ? { cno: n.obra.cno || null, inscricao_imobiliaria: n.obra.inscricao_imobiliaria || null } : null;
  return {
    ...n,
    destinatario: est
      ? { ...n.destinatario, documento: null, email: n.destinatario.email || null, nif: n.destinatario.nif || null }
      : soDigitos(n.destinatario?.documento)
        ? { ...n.destinatario, estrangeiro: false, endereco_exterior: null, email: n.destinatario.email || null }
        : { email: n.destinatario?.email || null },
    servico: { ...n.servico, nbs: n.servico.nbs || null, cnae: n.servico.cnae || null,
      codigo_tributacao_municipal: n.servico.codigo_tributacao_municipal || null,
      indicador_operacao: n.servico.indicador_operacao || null },
    municipio_prestacao: n.municipio_prestacao || null,
    obra,
    exportacao: est && n.iss.tributacao === 3 ? (n.exportacao || EXPORT_PADRAO) : null,
    retencoes: { ...n.retencoes, pis_cofins_cst: n.retencoes.pis_cofins_cst || null },
    informacoes_complementares: n.informacoes_complementares || null,
    enviar_email: Boolean(n.destinatario?.email),
  };
}

export default function NFSeForm({ emissor, onEmitida, notaRejeitada, notaSubstituida, modoRecorrente, onSalvarRecorrente }) {
  const base = notaRejeitada || notaSubstituida?.dados;
  const [n, setN] = useState(() => (base ? mesclar(base) : INICIAL));
  const [servicos, setServicos] = useState([]);
  const [previa, setPrevia] = useState(null);
  const [erroPrevia, setErroPrevia] = useState(null);
  const [sugIss, setSugIss] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const [subst, setSubst] = useState({ motivo_codigo: "99", motivo_texto: "" });
  const issManual = useRef(Boolean(base));

  const simples = SIMPLES.includes(String(emissor?.regime_tributario));
  const estrangeiro = Boolean(n.destinatario?.estrangeiro);
  const tomadorPJ = soDigitos(n.destinatario?.documento).length === 14;
  const servCat = servicos.find((s) => s.id === n.servico.servico_id);
  const permiteDeducao = Boolean(servCat?.permite_deducao || n.deducao_valor || n.deducao_percentual);

  useEffect(() => { fiscal.servicos().then(setServicos).catch(() => setServicos([])); }, []);

  const set = (c, v) => setN((x) => ({ ...x, [c]: v }));
  const setServ = (c, v) => setN((x) => ({ ...x, servico: { ...x.servico, [c]: v } }));
  const setIss = (c, v) => { issManual.current = true; setN((x) => ({ ...x, iss: { ...x.iss, [c]: v } })); };
  const setRet = (c, v) => setN((x) => ({ ...x, retencoes: { ...x.retencoes, [c]: v } }));
  const setObra = (c, v) => setN((x) => ({ ...x, obra: { ...x.obra, [c]: v } }));
  const setExp = (c, v) => setN((x) => ({ ...x, exportacao: { ...(x.exportacao || EXPORT_PADRAO), [c]: v } }));
  const setTom = (c, v) => setN((x) => ({ ...x, destinatario: { ...x.destinatario, [c]: v } }));
  const setExt = (c, v) => setN((x) => ({ ...x, destinatario: { ...x.destinatario, endereco_exterior: { ...EXT_VAZIO, ...(x.destinatario.endereco_exterior || {}), [c]: v } } }));

  const ctrib = soDigitos(n.servico.codigo_tributacao_nacional);
  const podeCalcular = n.valor_servico > 0 && ctrib.length === 6 && n.servico.discriminacao.trim().length >= 3;

  // Sugestão de ISS: onde é devido, alíquota daquele município e retenção pelo tomador
  useEffect(() => {
    if (ctrib.length !== 6 || estrangeiro) return setSugIss(null);
    const h = setTimeout(() => {
      fiscal.sugerirIss({
        codigo_tributacao_nacional: ctrib, servico_id: n.servico.servico_id,
        incidencia_local_prestacao: n.servico.incidencia_local_prestacao,
        municipio_prestacao: n.municipio_prestacao || null,
        tomador_documento: soDigitos(n.destinatario?.documento) || null,
        tomador_municipio: n.destinatario?.endereco?.codigo_municipio || null,
      }).then((r) => {
        setSugIss(r);
        if (!issManual.current) {
          setN((x) => ({ ...x, iss: { ...x.iss, retido: r.retido, ...(r.aliquota != null ? { aliquota: r.aliquota } : {}) } }));
        }
      }).catch(() => setSugIss(null));
    }, 400);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctrib, n.servico.servico_id, n.servico.incidencia_local_prestacao, n.municipio_prestacao, n.destinatario?.documento,
    n.destinatario?.endereco?.codigo_municipio, estrangeiro]);

  const aplicarSugestaoIss = () => {
    if (!sugIss) return;
    issManual.current = false;
    setN((x) => ({ ...x, iss: { ...x.iss, retido: sugIss.retido, ...(sugIss.aliquota != null ? { aliquota: sugIss.aliquota } : {}) } }));
  };

  // Prévia de ISS, retenções, IBS/CBS e valor líquido (calculada no servidor, mesma regra da emissão)
  useEffect(() => {
    if (!podeCalcular) return setPrevia(null);
    const h = setTimeout(() => {
      fiscal.calcularNfse(corpoNfse(n)).then((p) => { setPrevia(p); setErroPrevia(null); })
        .catch((e) => { setPrevia(null); setErroPrevia(e.message); });
    }, 400);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);

  const escolherServico = (id) => {
    const s = servicos.find((x) => x.id === id);
    if (!s) return setServ("servico_id", null);
    issManual.current = false;
    setN((x) => ({
      ...x,
      servico: { servico_id: s.id, codigo_tributacao_nacional: s.codigo_tributacao_nacional,
        codigo_tributacao_municipal: s.codigo_tributacao_municipal || "", nbs: s.nbs || "", cnae: s.cnae || "",
        discriminacao: x.servico.discriminacao || s.descricao, incidencia_local_prestacao: Boolean(s.incidencia_local_prestacao),
        ibs_cbs_cst: s.ibs_cbs_cst || "000", ibs_cbs_class_trib: s.ibs_cbs_class_trib || "000001",
        indicador_operacao: s.indicador_operacao || "" },
      iss: { ...x.iss, aliquota: s.aliquota_iss, retido: s.iss_retido },
      valor_servico: x.valor_servico || s.valor_padrao || 0,
    }));
  };

  // Retenções federais mais comuns entre PJ (IR 1,5%; CSRF 4,65% = PIS 0,65 + COFINS 3 + CSLL 1)
  const aplicarRetencoesPadrao = () => {
    const b = Number(n.valor_servico) - Number(n.desconto_incondicionado || 0);
    setN((x) => ({
      ...x,
      retencoes: { ...x.retencoes, pis_cofins_cst: x.retencoes.pis_cofins_cst || "01", aliquota_pis: 0.65,
        aliquota_cofins: 3, pis_cofins_retidos: true, irrf: round2(b * 0.015), csll: round2(b * 0.01) },
    }));
  };

  const alternarEstrangeiro = (sim) => {
    setN((x) => ({
      ...x,
      destinatario: sim ? { estrangeiro: true, nome: x.destinatario.nome || "", email: x.destinatario.email || "", nif: "", endereco_exterior: { ...EXT_VAZIO } }
        : { estrangeiro: false },
      iss: { ...x.iss, tributacao: sim ? 3 : 1, retido: false },
      exportacao: sim ? { ...EXPORT_PADRAO } : null,
    }));
  };

  const pendencias = [];
  if (!podeCalcular) pendencias.push("Serviço, código de tributação e valor");
  if (n.iss.retido && !soDigitos(n.destinatario?.documento)) pendencias.push("ISS retido exige tomador identificado");
  if (n.servico.incidencia_local_prestacao && !n.municipio_prestacao) pendencias.push("Município da prestação (ISS devido no local)");
  if (estrangeiro && !n.destinatario?.endereco_exterior?.pais) pendencias.push("País do tomador estrangeiro");
  if (!emissor?.inscricao_municipal) pendencias.push("Inscrição municipal do emissor (Configurações)");
  if (notaSubstituida && subst.motivo_texto.trim().length < 15) pendencias.push("Motivo da substituição (mín. 15 caracteres)");

  const emitir = async () => {
    setEnviando(true);
    setErro(null);
    try {
      if (modoRecorrente) {
        await onSalvarRecorrente(corpoNfse(n));
        return;
      }
      let r;
      if (notaSubstituida) {
        r = await fiscal.substituirNfse(notaSubstituida.id, { ...subst, nota: corpoNfse(n) });
      } else {
        const idReenvio = notaRejeitada?.id || (["rejeitada", "erro"].includes(resultado?.status) ? resultado.id : null);
        r = idReenvio ? await fiscal.reenviar(idReenvio, "nfse", corpoNfse(n)) : await fiscal.emitirNfse(corpoNfse(n));
      }
      setResultado(r);
      if (r.status === "autorizada") onEmitida?.(r);
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const titulo = modoRecorrente ? "Dados da NFS-e recorrente" : notaSubstituida ? `Substituir NFS-e nº ${notaSubstituida.numero ?? ""}`
    : notaRejeitada ? "Corrigir NFS-e" : "Nova NFS-e";
  const ext = n.destinatario?.endereco_exterior || EXT_VAZIO;
  const exp = n.exportacao || EXPORT_PADRAO;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">{titulo}</h1>
        {emissor?.ambiente === "homologacao" && !modoRecorrente && <span className="rounded bg-amber-100 px-2 py-1 text-xs text-amber-800">Homologação — sem valor fiscal</span>}
      </div>
      {notaRejeitada?.motivo_status && <Aviso tipo="erro">Retorno anterior: {notaRejeitada.motivo_status}</Aviso>}
      {modoRecorrente && <Aviso>Na discriminação você pode usar {"{mes}"}, {"{ano}"} e {"{competencia}"}; são trocados pelo mês de cada emissão.</Aviso>}

      {notaSubstituida && (
        <Cartao titulo="Motivo da substituição">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Campo rotulo="Motivo">
              <Selecao value={subst.motivo_codigo} onChange={(e) => setSubst((s) => ({ ...s, motivo_codigo: e.target.value }))}
                opcoes={[["01", "Desenquadramento do Simples Nacional"], ["02", "Enquadramento no Simples Nacional"],
                  ["03", "Inclusão retroativa de imunidade/isenção"], ["04", "Exclusão retroativa de imunidade/isenção"],
                  ["05", "Rejeição pelo tomador"], ["99", "Outros"]]} />
            </Campo>
            <Campo rotulo="Descrição" className="md:col-span-2" dica={`${subst.motivo_texto.length}/255`}>
              <Entrada value={subst.motivo_texto} maxLength={255} onChange={(e) => setSubst((s) => ({ ...s, motivo_texto: e.target.value }))} />
            </Campo>
          </div>
          <p className="mt-2 text-xs text-gray-500">A nova NFS-e substitui a anterior, que passa a constar como substituída. Prazo e regras variam por município.</p>
        </Cartao>
      )}

      <Cartao titulo="Tomador do serviço" acoes={
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={estrangeiro} onChange={(e) => alternarEstrangeiro(e.target.checked)} /> Tomador no exterior (exportação)
        </label>
      }>
        {estrangeiro ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <Campo rotulo="Nome / Razão social" className="md:col-span-3"><Entrada value={n.destinatario.nome || ""} onChange={(e) => setTom("nome", e.target.value)} /></Campo>
            <Campo rotulo="NIF (identificação fiscal)" className="md:col-span-2" dica="Vazio = país não exige"><Entrada value={n.destinatario.nif || ""} onChange={(e) => setTom("nif", e.target.value)} /></Campo>
            <Campo rotulo="País (ISO)" dica="Ex.: US, PT"><Entrada value={ext.pais} maxLength={2} onChange={(e) => setExt("pais", e.target.value.toUpperCase())} /></Campo>
            <Campo rotulo="Cidade" className="md:col-span-2"><Entrada value={ext.cidade} onChange={(e) => setExt("cidade", e.target.value)} /></Campo>
            <Campo rotulo="Estado/Província" className="md:col-span-2"><Entrada value={ext.estado_provincia} onChange={(e) => setExt("estado_provincia", e.target.value)} /></Campo>
            <Campo rotulo="Código postal"><Entrada value={ext.codigo_postal} onChange={(e) => setExt("codigo_postal", e.target.value)} /></Campo>
            <Campo rotulo="E-mail"><Entrada type="email" value={n.destinatario.email || ""} onChange={(e) => setTom("email", e.target.value)} /></Campo>
            <Campo rotulo="Logradouro" className="md:col-span-3"><Entrada value={ext.logradouro} onChange={(e) => setExt("logradouro", e.target.value)} /></Campo>
            <Campo rotulo="Número"><Entrada value={ext.numero} onChange={(e) => setExt("numero", e.target.value)} /></Campo>
            <Campo rotulo="Bairro/Distrito" className="md:col-span-2"><Entrada value={ext.bairro} onChange={(e) => setExt("bairro", e.target.value)} /></Campo>
          </div>
        ) : (
          <>
            <Destinatario valor={n.destinatario} onChange={(d) => set("destinatario", d)} modelo="nfse" />
            <p className="mt-2 text-xs text-gray-500">Para pessoa física o tomador é opcional. Com o município do tomador, o sistema confere se ele é obrigado a reter o ISS.</p>
          </>
        )}
      </Cartao>

      <Cartao titulo="Serviço">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <Campo rotulo="Serviço cadastrado" className="md:col-span-3" dica={servicos.length ? undefined : "Cadastre seus serviços em Configurações para preencher tudo de uma vez"}>
            <Selecao value={n.servico.servico_id || ""} vazio="Digitar manualmente" onChange={(e) => escolherServico(e.target.value)}
              opcoes={servicos.map((s) => [s.id, `${s.codigo} — ${s.descricao.slice(0, 60)}`])} />
          </Campo>
          <Campo rotulo="Cód. tributação nacional" dica="6 dígitos (LC 116)">
            <Entrada value={n.servico.codigo_tributacao_nacional} maxLength={8} onChange={(e) => setServ("codigo_tributacao_nacional", soDigitos(e.target.value))} />
          </Campo>
          <Campo rotulo="Cód. tributação municipal" dica="Se o município exigir">
            <Entrada value={n.servico.codigo_tributacao_municipal} onChange={(e) => setServ("codigo_tributacao_municipal", e.target.value)} />
          </Campo>
          <Campo rotulo="NBS">
            <Entrada value={n.servico.nbs} maxLength={9} onChange={(e) => setServ("nbs", soDigitos(e.target.value))} />
          </Campo>
          <Campo rotulo="Discriminação do serviço" className="md:col-span-6" dica={`${n.servico.discriminacao.length}/2000`}>
            <Texto rows={4} maxLength={2000} value={n.servico.discriminacao} onChange={(e) => setServ("discriminacao", e.target.value)} />
          </Campo>
          {!modoRecorrente && <Campo rotulo="Competência"><Entrada type="date" value={n.competencia || ""} onChange={(e) => set("competencia", e.target.value)} /></Campo>}
          <Campo rotulo="Onde o ISS é devido" className="md:col-span-2">
            <Selecao value={n.servico.incidencia_local_prestacao ? "1" : "0"} onChange={(e) => setServ("incidencia_local_prestacao", e.target.value === "1")}
              opcoes={[["0", "No meu município (regra geral)"], ["1", "No local da prestação (art. 3º)"]]} />
          </Campo>
          <Campo rotulo="Município da prestação (IBGE)" dica={n.servico.incidencia_local_prestacao ? "Obrigatório para este serviço" : `Vazio = ${emissor?.endereco?.municipio || "município do emissor"}`}>
            <Entrada value={n.municipio_prestacao} maxLength={7} onChange={(e) => set("municipio_prestacao", soDigitos(e.target.value))} />
          </Campo>
          <Campo rotulo="cIndOp (IBS/CBS)" dica="Do cadastro do serviço">
            <Entrada value={n.servico.indicador_operacao || ""} maxLength={6} onChange={(e) => setServ("indicador_operacao", soDigitos(e.target.value))} />
          </Campo>
          <Campo rotulo="Tomador é consumidor final?" dica="IBS/CBS">
            <Selecao value={n.consumidor_final ? "1" : "0"} onChange={(e) => set("consumidor_final", e.target.value === "1")} opcoes={[["0", "Não"], ["1", "Sim"]]} />
          </Campo>
        </div>
      </Cartao>

      {(permiteDeducao || n.obra?.cno || n.obra?.inscricao_imobiliaria) && (
        <Cartao titulo="Construção civil: deduções e obra">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <Campo rotulo="Dedução em valor (R$)" dica="Materiais, subempreitadas…"><Valor value={n.deducao_valor} onChange={(v) => setN((x) => ({ ...x, deducao_valor: v, deducao_percentual: 0 }))} /></Campo>
            <Campo rotulo="ou dedução em %"><Numero value={n.deducao_percentual} onChange={(v) => setN((x) => ({ ...x, deducao_percentual: Number(v), deducao_valor: 0 }))} /></Campo>
            <Campo rotulo="CNO da obra"><Entrada value={n.obra?.cno || ""} onChange={(e) => setObra("cno", e.target.value)} /></Campo>
            <Campo rotulo="ou inscrição imobiliária"><Entrada value={n.obra?.inscricao_imobiliaria || ""} onChange={(e) => setObra("inscricao_imobiliaria", e.target.value)} /></Campo>
          </div>
        </Cartao>
      )}

      {estrangeiro && (
        <Cartao titulo="Exportação de serviço (comércio exterior)">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <Campo rotulo="Modo de prestação">
              <Selecao value={exp.modo_prestacao} onChange={(e) => setExp("modo_prestacao", Number(e.target.value))}
                opcoes={[[1, "Transfronteiriço"], [2, "Consumo no Brasil"], [3, "Presença comercial no exterior"], [4, "Movimento temporário de pessoas"]]} />
            </Campo>
            <Campo rotulo="Vínculo com o tomador">
              <Selecao value={exp.vinculo} onChange={(e) => setExp("vinculo", Number(e.target.value))}
                opcoes={[[0, "Sem vínculo"], [1, "Controlada"], [2, "Controladora"], [3, "Coligada"], [4, "Matriz"], [5, "Filial ou sucursal"], [6, "Outro vínculo"]]} />
            </Campo>
            <Campo rotulo="Moeda (código BACEN)" dica="220 = dólar, 978 = euro"><Entrada value={exp.moeda} maxLength={3} onChange={(e) => setExp("moeda", soDigitos(e.target.value))} /></Campo>
            <Campo rotulo="Valor na moeda estrangeira"><Valor value={exp.valor_moeda} onChange={(v) => setExp("valor_moeda", v)} /></Campo>
          </div>
          <p className="mt-2 text-xs text-gray-500">Na exportação de serviço não incide ISS; o resultado precisa se verificar no exterior.</p>
        </Cartao>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Cartao titulo="Valores e ISS" acoes={sugIss && issManual.current && <Botao variante="fantasma" onClick={aplicarSugestaoIss}>Usar sugestão</Botao>}>
          <div className="grid grid-cols-2 gap-3">
            <Campo rotulo="Valor do serviço"><Valor value={n.valor_servico} onChange={(v) => set("valor_servico", v)} /></Campo>
            <Campo rotulo="Desconto incondicionado" dica="Reduz a base do ISS"><Valor value={n.desconto_incondicionado} onChange={(v) => set("desconto_incondicionado", v)} /></Campo>
            <Campo rotulo="Desconto condicionado" dica="Não reduz a base"><Valor value={n.desconto_condicionado} onChange={(v) => set("desconto_condicionado", v)} /></Campo>
            <Campo rotulo="Tributação do ISS">
              <Selecao value={n.iss.tributacao} onChange={(e) => setIss("tributacao", Number(e.target.value))}
                opcoes={[[1, "Operação tributável"], [2, "Imunidade"], [3, "Exportação de serviço"], [4, "Não incidência"]]} />
            </Campo>
            <Campo rotulo="Alíquota ISS (%)" dica={simples ? (n.iss.retido ? "Alíquota efetiva do ISS no Simples" : "No Simples, só vai na nota se houver retenção") : "Entre 2% e 5%"}>
              <Numero value={n.iss.aliquota} onChange={(v) => setIss("aliquota", Number(v))} />
            </Campo>
            <Campo rotulo="ISS retido pelo tomador?">
              <Selecao value={n.iss.retido ? "1" : "0"} onChange={(e) => setIss("retido", e.target.value === "1")} opcoes={[["0", "Não"], ["1", "Sim"]]} />
            </Campo>
          </div>
          {sugIss && (
            <div className="mt-3 space-y-1 text-xs">
              {sugIss.motivos.map((m) => <p key={m} className="text-blue-900">• {m}</p>)}
              {sugIss.avisos.map((m) => <p key={m} className="text-amber-800">⚠ {m}</p>)}
            </div>
          )}
        </Cartao>

        <Cartao titulo="Retenções federais" acoes={tomadorPJ && !simples && <Botao variante="fantasma" onClick={aplicarRetencoesPadrao}>Aplicar padrão PJ</Botao>}>
          <div className="grid grid-cols-2 gap-3">
            <Campo rotulo="CST PIS/COFINS" dica="Vazio = não informar">
              <Selecao value={n.retencoes.pis_cofins_cst} vazio="Não informar" onChange={(e) => setRet("pis_cofins_cst", e.target.value)}
                opcoes={[["01", "01 — Tributável"], ["06", "06 — Alíquota zero"], ["07", "07 — Isenta"], ["08", "08 — Sem incidência"], ["09", "09 — Suspensão"], ["49", "49 — Outras"]]} />
            </Campo>
            <Campo rotulo="PIS/COFINS retidos?">
              <Selecao value={n.retencoes.pis_cofins_retidos ? "1" : "0"} onChange={(e) => setRet("pis_cofins_retidos", e.target.value === "1")} opcoes={[["0", "Não"], ["1", "Sim"]]} />
            </Campo>
            <Campo rotulo="% PIS"><Numero value={n.retencoes.aliquota_pis} onChange={(v) => setRet("aliquota_pis", Number(v))} /></Campo>
            <Campo rotulo="% COFINS"><Numero value={n.retencoes.aliquota_cofins} onChange={(v) => setRet("aliquota_cofins", Number(v))} /></Campo>
            <Campo rotulo="IRRF retido (R$)"><Valor value={n.retencoes.irrf} onChange={(v) => setRet("irrf", v)} /></Campo>
            <Campo rotulo="CSLL retida (R$)"><Valor value={n.retencoes.csll} onChange={(v) => setRet("csll", v)} /></Campo>
            <Campo rotulo="INSS retido (R$)"><Valor value={n.retencoes.inss} onChange={(v) => setRet("inss", v)} /></Campo>
          </div>
        </Cartao>
      </div>

      <Campo rotulo="Informações complementares">
        <Texto value={n.informacoes_complementares} maxLength={2000} onChange={(e) => set("informacoes_complementares", e.target.value)} />
      </Campo>

      <div className="sticky bottom-0 z-10 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <dl className="flex flex-wrap gap-6 text-sm">
            <div><dt className="text-gray-500">Valor do serviço</dt><dd className="font-semibold tabular-nums">{brl(previa?.valor_servicos ?? n.valor_servico)}</dd></div>
            {previa?.valor_deducao > 0 && <div><dt className="text-gray-500">Deduções</dt><dd className="tabular-nums">{brl(previa.valor_deducao)}</dd></div>}
            <div><dt className="text-gray-500">Base ISS</dt><dd className="tabular-nums">{brl(previa?.base_iss)}</dd></div>
            <div><dt className="text-gray-500">ISS{previa?.iss_retido ? " (retido)" : ""}</dt><dd className="tabular-nums">{brl(previa?.valor_iss)}</dd></div>
            <div><dt className="text-gray-500">Retenções</dt><dd className="tabular-nums">{brl(previa?.valor_retencoes)}</dd></div>
            {previa?.ibs_cbs_incluido && <div><dt className="text-gray-500">IBS + CBS</dt><dd className="tabular-nums">{brl((previa.valor_ibs || 0) + (previa.valor_cbs || 0))}</dd></div>}
            <div><dt className="text-gray-500">Valor líquido</dt><dd className="text-lg font-bold tabular-nums">{brl(previa?.valor_liquido)}</dd></div>
          </dl>
          <Botao carregando={enviando} disabled={pendencias.length > 0} onClick={emitir} className="px-6">
            {modoRecorrente ? "Salvar recorrência" : notaSubstituida ? "Emitir NFS-e substituta"
              : notaRejeitada || ["rejeitada", "erro"].includes(resultado?.status) ? "Reenviar NFS-e" : "Emitir NFS-e"}
          </Botao>
        </div>
        {pendencias.length > 0 && <p className="mt-2 text-xs text-gray-500">Pendências: {pendencias.join(" · ")}</p>}
        {previa?.ibs_cbs_incluido && !n.servico.indicador_operacao && (
          <p className="mt-2 text-xs text-amber-800">IBS/CBS já é obrigatório para você: cadastre o cIndOp do serviço. Sem ele a nota sai sem o grupo (aceito só até 31/12/2026).</p>
        )}
        {erroPrevia && <div className="mt-3"><Aviso tipo="atencao">{erroPrevia}</Aviso></div>}
        {erro && <div className="mt-3"><Aviso tipo="erro">{erro}</Aviso></div>}
        {resultado && <div className="mt-3"><ResultadoNFSe nota={resultado} /></div>}
      </div>
    </div>
  );
}

function mesclar(nota) {
  return {
    ...INICIAL, ...nota,
    destinatario: nota.destinatario || {},
    servico: { ...INICIAL.servico, ...(nota.servico || {}), indicador_operacao: nota.servico?.indicador_operacao || "" },
    iss: { ...INICIAL.iss, ...(nota.iss || {}) },
    retencoes: { ...INICIAL.retencoes, ...(nota.retencoes || {}), pis_cofins_cst: nota.retencoes?.pis_cofins_cst || "" },
    obra: { ...INICIAL.obra, ...(nota.obra || {}) },
    deducao_valor: nota.deducao_valor || 0,
    deducao_percentual: nota.deducao_percentual || 0,
    municipio_prestacao: nota.municipio_prestacao || "",
    informacoes_complementares: nota.informacoes_complementares || "",
  };
}

export function ResultadoNFSe({ nota }) {
  if (nota.status === "autorizada") {
    return (
      <Aviso tipo="sucesso">
        NFS-e nº {nota.numero} autorizada{nota.codigo_verificacao ? ` · código de verificação ${nota.codigo_verificacao}` : ""}.
        <span className="ml-2 inline-flex gap-3">
          <button type="button" className="underline" onClick={() => abrirArquivo(`/notas/${nota.id}/pdf`)}>Abrir PDF</button>
          {nota.link_consulta && <a className="underline" href={nota.link_consulta} target="_blank" rel="noreferrer">Consultar na prefeitura</a>}
        </span>
      </Aviso>
    );
  }
  if (nota.status === "processando") {
    return <Aviso tipo="atencao">DPS enviada e em processamento na prefeitura. Acompanhe na lista de notas (Buscar protocolo).</Aviso>;
  }
  return <Aviso tipo="erro">NFS-e {nota.status}: {nota.motivo_status}{"\n"}Corrija os dados e clique em Reenviar.</Aviso>;
}
